import { Alert, Box, Card, CardContent, CardHeader, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import StrippedDataGrid from "components/styled";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetDeviceLogQuery } from "services/data/deviceApi";
import styles  from './Logs.module.css';
import { gridClasses, GridFooter, GridFooterContainer } from "@mui/x-data-grid";
import { DateField, DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { ru } from "date-fns/locale";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parse } from "date-fns";
import { isoToDateTimeString, toIsoString } from "utils/date";

const DEFAULT_ERROR = "Ошибка соединения с сервером";
const PAGE_SIZE = 5;
const dateFormat = "dd MMMM yyyy";
const referenceDate = new Date(1970, 0, 1, 0, 0, 0);


const Footer = ({date, setDate}) => {
  
  //const [date, setDate] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false)

  const onChangeDate = (dateObject, context) => { 
    if(dateObject === null) { 
      setDate(null);
      queueMicrotask(() => document.activeElement?.blur());
      return;
    }
    !context.validationError && setDate( format( dateObject, dateFormat ) );
  };

  return (
    <GridFooterContainer  sx={{py: 1, justifyContent: 'center'}}>
      <Box sx={{width: '50%', display: 'flex', justifyContent: 'flex-end'}}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
          <DatePicker 
            enableAccessibleFieldDOMStructure={false}
            format={dateFormat}
            label={ (date || pickerOpen) ? "" : "За все время"}
            value={date ? parse(date, dateFormat, referenceDate) : null}
            disableFuture
            onChange={onChangeDate}
            open={pickerOpen}
            onClose={() => {setPickerOpen(false); queueMicrotask(() => document.activeElement?.blur());}}
            slotProps={{
              field: {
                selectedSections: null,
                placeholder: null,
                clearable: true
              },
              textField: {
                inputProps: {
                  readOnly: true,
                  tabIndex: -1
                },
                InputLabelProps: {
                  shrink: pickerOpen
                },
                InputProps:{
                  notched: false
                },
                onClick: () => {if(!pickerOpen) setPickerOpen(true);queueMicrotask(() => document.activeElement?.blur());},
              },
              openPickerButton: {
                onClick: () => {if(!pickerOpen) setPickerOpen(true);queueMicrotask(() => document.activeElement?.blur());}
              }
            }}
            sx={{
              minWidth: 300,
              ...(date === null && {
                '& .MuiPickersSectionList-root': {
                  color: 'transparent'
                }
              })
            }}/>
        </LocalizationProvider>
      </Box>
      <Box sx={{width: '50%', display: 'flex', justifyContent: 'flex-end'}}>
        <GridFooter sx={{border: 0}}/>
      </Box>
    </GridFooterContainer>
  );
};

const Logs = () => {
  const { id } = useParams();
  const [date, setDate] = useState(null);
  const cursorStack = useRef([]);
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: PAGE_SIZE});
  const [rowCount, setRowCount] = useState(0);
  const prevParams = useRef(null);

  const pageOptions = useMemo( ()=> {
    return {
      cursor: cursorStack.current[paginationModel.page - 1] ?? "",
      limit: paginationModel.pageSize
    };
  }, [paginationModel]);

  const makeParams = useMemo( () => {
    let params = {
      id,
      sort: [{field: 'createdAt', value: 'desc'}],
      pageInfo: pageOptions
    };
    if(date) {
      const from = parse(date, dateFormat, referenceDate);
      const to = parse(date, dateFormat, referenceDate);
      to.setHours(23,59,59,999);
      params['filter'] = [ {field: 'createdAt', op: 'between', value: [toIsoString(from), toIsoString(to)]} ];
    }
    
    return params;
  }, [id, pageOptions, date]);

  const { data: {body: {items, pageInfo: {hasNextPage, nextCursor} = {}} = {}} = {}, error, isLoading } = useGetDeviceLogQuery(makeParams);

  useEffect(() =>{
    prevParams.current = makeParams
  }, [makeParams]);

  
  const columns = useMemo( () => [
      {field: 'createdAt', type: 'date', display: 'flex', width: 140, valueFormatter: (v) => isoToDateTimeString(v)},
      {field: 'text', flex: 1}
    ], [items]
  );

  useEffect(() => {
    if(!isLoading && nextCursor) {
      cursorStack.current[paginationModel.page] = nextCursor;
    }
  }, [paginationModel.page, isLoading, nextCursor]);

  useEffect(() => {
    setPaginationModel((prev) => ({...prev, page: 0}));
  }, [date, id]);

  //Мемоизируем для случайного мерцания, если во время получения данных hasNextPage выставлено в undefined
  const paginationMetaRef = useRef(undefined);
  const paginationMeta = useMemo(()=> {
    if(hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage };
    }
    return paginationMetaRef.current;
  }, [hasNextPage]);

  const onPaginationModelChange = (model) => { 
    if(model.page === 0 || cursorStack.current[model.page-1]) {
      setPaginationModel(model);
    }
  };

  useEffect(() => {
    if(paginationMeta?.hasNextPage !== false) {
      setRowCount(-1);
    }
  }, [paginationMeta?.hasNextPage, rowCount]);

  return (
    <Card sx={{maxHeight: '100%', height: '100%', display: 'flex', flexDirection: 'column'}}>
      <CardContent sx={{height: '100%', overflow: 'auto'}}>
        <StrippedDataGrid
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          paginationMeta={paginationMeta}
          pageSizeOptions={[PAGE_SIZE]}
          isLoading={isLoading}
          rowCount={rowCount}
          onRowCountChange={(rowCount) => setRowCount(rowCount)}
          getRowId={(row) => row.createdAt}
          getRowHeight={() => 'auto'}
          getRowClassName={({indexRelativeToCurrentPage}) => indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'}
          rows={items}         
          localeText={{
            noRowsLabel: error?.data?.message ?? "Логов пока нет",
            MuiTablePagination: {
              labelDisplayedRows: ({from, to}) => `События ${from} - ${to}`
            }
          }}
          columns={columns}
          columnHeaderHeight={1}
          disableColumnResize
          disableColumnMenu
          disableRowSelectionOnClick
          disableMultipleRowSelection
          slots={{
            footer: Footer
          }}
          slotProps={{
            footer: {
              date, setDate
            }
          }}
          sx={{
            width: '100%',
            [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
              outline: 'none',
            },
            [`& .${gridClasses.columnHeader}, & .MuiInputBase-root`]: {
              overflowX: 'unset'
            },
            [`& .${gridClasses.cell}`]: {
              whiteSpace: 'normal !important',
              wordBreak: 'break-word',
              alignItems: 'center',
              py: 1  
            }
          }}
        />
      </CardContent>
    </Card>
      
  );
};

export default Logs;